/**
 * items_silver_gallery.js
 * Swiper + Fancybox + Plyr gallery for the items-silver widget.
 * Auto-bundled by Vite's import.meta.glob — do NOT import manually.
 *
 * Data contract (emitted by items_silver.jinja):
 *   <script type="application/json" data-items-gallery-config>{ minImageHeight, hasMultiple, initialSlide, altBase }</script>
 *   <script type="application/json" data-items-gallery-data>[ { src, thumb, caption, type? }, … ]</script>
 */

let _galleryData = [];
let _galleryConfig = {};
let _currentImageIndex = 0;
let _mainSwiper = null;
let _plyrInstances = {};
let _fancyboxPlyrInstances = {};

function extractYoutubeId(url) {
    if (url.includes('youtu.be/'))           return url.split('youtu.be/')[1].split('?')[0];
    if (url.includes('watch?v='))            return url.split('watch?v=')[1].split('&')[0];
    if (url.includes('youtube.com/embed/'))  return url.split('youtube.com/embed/')[1].split('?')[0];
    if (url.includes('youtube.com/shorts/')) return url.split('youtube.com/shorts/')[1].split('?')[0];
    return '';
}

function convertToYoutubeEmbed(url) {
    const id = extractYoutubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : url;
}

function stopAllVideos() {
    Object.values(_plyrInstances).forEach(player => {
        try { if (player && typeof player.pause === 'function') player.pause(); }
        catch (e) { console.error('Error pausing player:', e); }
    });
}

function stopAllFancyboxVideos() {
    Object.values(_fancyboxPlyrInstances).forEach(player => {
        try { if (player && typeof player.pause === 'function') player.pause(); }
        catch (e) { console.error('Error pausing fancybox player:', e); }
    });
}

function destroyAllFancyboxPlyr() {
    Object.keys(_fancyboxPlyrInstances).forEach(key => {
        try {
            if (_fancyboxPlyrInstances[key] && typeof _fancyboxPlyrInstances[key].destroy === 'function')
                _fancyboxPlyrInstances[key].destroy();
        } catch (e) { console.error('Error destroying fancybox player:', e); }
        delete _fancyboxPlyrInstances[key];
    });
}

function updateMainImage(url, title, element, index, mediaType) {
    _currentImageIndex = index;

    document.querySelectorAll('.thumbnail-display').forEach(el => el.classList.remove('ring-2', 'ring-primary'));
    element.classList.add('ring-2', 'ring-primary');

    if (_galleryConfig.hasMultiple) {
        if (_mainSwiper) _mainSwiper.slideTo(index);
        return;
    }

    // Single-image fallback: replace the main media in-place
    const mainContainer = document.getElementById('main-image-container');
    if (!mainContainer) return;

    const isYoutube = url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo');
    const isVideo   = mediaType === 'video' || mediaType.startsWith('video/');

    if (isVideo) {
        if (!isYoutube) {
            mainContainer.innerHTML = `<video id="player" playsinline controls><source src="${url}" type="video/mp4" /></video>`;
        } else {
            const embedUrl = convertToYoutubeEmbed(url);
            mainContainer.innerHTML = `<div id="player"><iframe src="${embedUrl}" id="main-media" class="w-full h-auto max-w-full max-h-[70vh] object-contain mx-auto aspect-video" allowtransparency allow="autoplay"></iframe></div>`;
        }
        mainContainer.onclick = null;
        mainContainer.classList.remove('cursor-zoom-in');
    } else {
        const altBase = _galleryConfig.altBase || '';
        mainContainer.innerHTML = `
            <button type="button" class="w-full cursor-zoom-in focus:outline-none" aria-label="Open image in gallery" onclick="openGallery(${index})">
                <img id="main-media" src="${url}" alt="${title || altBase}" class="w-full h-auto max-w-full max-h-[70vh] object-contain mx-auto justify-start" fetchpriority="high" />
            </button>`;
        mainContainer.onclick = null;
        mainContainer.classList.remove('cursor-zoom-in');
    }
}

function _buildTempVideoContainers() {
    const containers = [];
    _galleryData.forEach((item, idx) => {
        if (!item._videoProvider) return;
        const wrap = document.createElement('div');
        wrap.id = `video-${idx}`;
        wrap.style.display = 'none';
        if (item._videoProvider === 'youtube' || item._videoProvider === 'vimeo') {
            const embedSrc = item._videoProvider === 'youtube'
                ? `https://www.youtube.com/embed/${item._videoId}?iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0&enablejsapi=1`
                : `https://player.vimeo.com/video/${item._videoId}?loop=false&byline=false&portrait=false&title=false&speed=true&transparent=0&gesture=media`;
            wrap.innerHTML = `<div class="plyr__video-embed w-full" id="fancybox-player-${idx}" data-plyr-provider="${item._videoProvider}" data-plyr-embed-id="${item._videoId}"><iframe src="${embedSrc}" allowfullscreen allowtransparency allow="autoplay"></iframe></div>`;
        } else {
            wrap.innerHTML = `<video id="fancybox-player-${idx}" playsinline controls${item.thumb ? ` poster="${item.thumb}"` : ''}><source src="${item._videoUrl}" type="video/mp4" /></video>`;
        }
        document.body.appendChild(wrap);
        containers.push(wrap);
    });
    return containers;
}

function openGallery(startIndex = null) {
    if (!_galleryData.length) return;
    const tempContainers = _buildTempVideoContainers();
    Fancybox.show(_galleryData, {
        startIndex: startIndex !== null ? startIndex : _currentImageIndex,
        theme: 'dark',
        dragToClose: false,
        placeFocusBack: false,
        trapFocus: false,
        Html: { videoTpl: false, youtube: false, vimeo: false },
        on: {
            reveal: (fancybox, slide) => {
                const slideIndex = slide.index;
                const container  = document.getElementById(`video-${slideIndex}`);
                if (!container) return;
                const playerElement = container.querySelector(`#fancybox-player-${slideIndex}`);
                if (playerElement && !_fancyboxPlyrInstances[slideIndex]) {
                    try {
                        const isEmbed = playerElement.classList.contains('plyr__video-embed');
                        const opts = isEmbed
                            ? { controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'], youtube: { noCookie: true, rel: 0, showinfo: 0, iv_load_policy: 3, modestbranding: 1, controls: 1, disablekb: 0, fs: 1 }, vimeo: { byline: false, portrait: false, title: false } }
                            : { controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'] };
                        _fancyboxPlyrInstances[slideIndex] = new Plyr(playerElement, opts);
                    } catch (e) { console.error(`Error initialising Plyr for slide ${slideIndex}:`, e); }
                }
            },
            'Carousel.change': (fancybox, carousel, to, from) => {
                if (from !== undefined && _fancyboxPlyrInstances[from]) {
                    try { _fancyboxPlyrInstances[from].pause(); }
                    catch (e) { console.error(`Error pausing video on slide ${from}:`, e); }
                }
            },
            closing: () => {
                stopAllFancyboxVideos();
                destroyAllFancyboxPlyr();
                tempContainers.forEach(el => el.remove());
            },
        },
        Carousel: {
            Toolbar: {
                display: {
                    left: ['counter'],
                    right: ['fullscreen', 'thumbs', 'close'],
                },
            },
            breakpoints: {
                '(min-width: 768px)': {
                    Toolbar: {
                        display: {
                            left: ['counter'],
                            middle: ['zoomIn', 'zoomOut', 'toggle1to1', 'rotateCCW', 'rotateCW', 'fullscreen', 'flipX', 'flipY', 'reset'],
                            right: ['thumbs', 'close'],
                        },
                    },
                },
            },
            Images: { zoom: true, fit: 'contain', initialSize: 'fit', protected: false },
            Zoomable: {
                startPos: { scale: window.innerWidth <= 768 ? 'fit' : 'cover' },
                Panzoom:  { protected: true, maxScale: 5, minScale: window.innerWidth <= 768 ? 1 : 1.5 },
            },
            transition: 'slide',
            Thumbs: {
                type: 'classic',
                Carousel: {
                    center: ref => !ref.isVertical() || ref.getTotalSlideDim() > ref.getViewportDim(),
                    vertical: false,
                    breakpoints: { '(min-width: 640px)': { vertical: true } },
                },
            },
        },
    });
}

function _initMinHeight() {
    const minHeight = _galleryConfig.minImageHeight;
    if (!minHeight) return;
    const container = document.getElementById('main-image-container') || document.querySelector('.main-image-swiper');
    if (!container) return;
    function applyMinHeight() {
        container.style.minHeight = window.innerWidth >= 768 ? minHeight : '';
    }
    applyMinHeight();
    window.addEventListener('resize', applyMinHeight);
}

function _initSwiper() {
    if (!_galleryConfig.hasMultiple) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    _mainSwiper = new Swiper('.main-image-swiper', {
        loop: false,
        speed: reducedMotion ? 0 : 300,
        navigation:  { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
        pagination:  { el: '.swiper-pagination', clickable: true },
        initialSlide: _currentImageIndex,
        keyboard:    { enabled: true },
        touchEventsTarget: 'container',
        simulateTouch: true,
        allowTouchMove: true,
        touchRatio: 1,
        touchAngle: 45,
        preventClicks: false,
        preventClicksPropagation: false,
        slideToClickedSlide: false,
        on: {
            slideChange() {
                const realIndex = this.realIndex;
                _currentImageIndex = realIndex;
                stopAllVideos();

                document.querySelectorAll('.thumbnail-display').forEach((el, idx) => {
                    el.classList.toggle('ring-2', idx === realIndex);
                    el.classList.toggle('ring-primary', idx === realIndex);
                });

                const currentSlide = this.slides[this.activeIndex];
                const hasVideo = currentSlide.querySelector('video') || currentSlide.querySelector('.plyr__video-embed');
                if (hasVideo && _plyrInstances[realIndex]) {
                    setTimeout(() => {
                        try { _plyrInstances[realIndex].play(); }
                        catch (e) { console.error('Error playing video:', e); }
                    }, 300);
                }
            },
        },
    });

    document.querySelectorAll('.main-image-swiper .swiper-slide').forEach(slide => {
        const slideIndex = parseInt(slide.getAttribute('data-slide-index'));
        const embedDiv   = slide.querySelector('.plyr__video-embed');
        const videoEl    = slide.querySelector('video');

        if (embedDiv) {
            try {
                _plyrInstances[slideIndex] = new Plyr(embedDiv, {
                    controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
                    youtube:  { noCookie: true, rel: 0, showinfo: 0, iv_load_policy: 3, modestbranding: 1, controls: 1, disablekb: 0, fs: 1 },
                    vimeo:    { byline: false, portrait: false, title: false },
                });
            } catch (e) { console.error('Error initialising Plyr for embed:', e); }
        } else if (videoEl) {
            try {
                _plyrInstances[slideIndex] = new Plyr(videoEl, {
                    controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
                });
            } catch (e) { console.error('Error initialising Plyr for video:', e); }
        }
    });
}

function _initThumbnailListeners() {
    if (!_galleryConfig.hasMultiple) return;
    document.querySelectorAll('.thumbnail-display').forEach(display => {
        display.addEventListener('click', function (event) {
            event.preventDefault();
            updateMainImage(
                this.getAttribute('data-url'),
                this.getAttribute('data-title'),
                this,
                parseInt(this.getAttribute('data-index')),
                this.getAttribute('data-type') || 'image',
            );
        });
    });
}

function initItemGallery() {
    const configEl = document.querySelector('[data-items-gallery-config]');
    const dataEl   = document.querySelector('[data-items-gallery-data]');
    if (!configEl || !dataEl) return;

    _galleryConfig       = JSON.parse(configEl.textContent);
    _galleryData         = JSON.parse(dataEl.textContent);
    _currentImageIndex   = _galleryConfig.initialSlide || 0;
    _plyrInstances       = {};
    _fancyboxPlyrInstances = {};
    _mainSwiper          = null;

    _initMinHeight();
    _initSwiper();
    _initThumbnailListeners();
}

window.openGallery    = openGallery;
window.updateMainImage = updateMainImage;

document.addEventListener('DOMContentLoaded', initItemGallery);
